import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Crown, Shirt, ShoppingBag, ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Category {
  title: string;
  tagline: string;
  description: string;
  tags: string[];
  cta: string;
  link: string;
  accent: 'coral' | 'teal' | 'golden' | 'charcoal';
  icon: React.ElementType;
  image: string;
  /** Optional autoplay loop video shown instead of the image (uses image as poster/fallback). */
  video?: string;
}

const categories: Category[] = [
  {
    title: 'Travel Styling',
    tagline: 'Look your best at every destination',
    description:
      'Destination-aware outfit planning that takes the stress out of packing. From airport looks to resort-ready ensembles, get weather-smart wardrobe picks and curated packing checklists for every trip.',
    tags: ['Airport Looks', 'Destination Outfits', 'Packing Lists', 'Weather-Smart'],
    cta: 'Explore Travel Styling',
    link: '/travel-styling',
    accent: 'coral',
    icon: Plane,
    image:
      'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80&auto=format&fit=crop',
    video: '/suitcase_travel.mp4',
  },
  {
    title: 'Event Styling',
    tagline: 'Make every occasion unforgettable',
    description:
      'Weddings, parties, formal galas, cocktail nights. Never stress over a dress code again. Get curated looks that match the occasion, venue, and your personal flair.',
    tags: ['Weddings', 'Formal Events', 'Cocktail Nights', 'Dress Code Guide'],
    cta: 'Explore Event Styling',
    link: '/event-styling',
    accent: 'teal',
    icon: Crown,
    image:
      'https://images.unsplash.com/photo-1492707892479-7bc8d5a4ee93?w=1200&q=80&auto=format&fit=crop',
    video: '/suitcase_events.mp4',
  },
  {
    title: 'Personal Styling',
    tagline: 'Elevate your everyday wardrobe',
    description:
      'Build workwear capsules, discover your signature style, and refresh your wardrobe with intention. Weekend looks, office-ready outfits, and hero pieces, all tailored to you.',
    tags: ['Workwear', 'Capsule Wardrobe', 'Weekend Looks', 'Style Discovery'],
    cta: 'Explore Personal Styling',
    link: '/personal-styling',
    accent: 'golden',
    icon: Shirt,
    image:
      'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1200&q=80&auto=format&fit=crop',
    video: '/suitcase_personal.mp4',
  },
  {
    title: 'Personal Shopping',
    tagline: 'A stylist by your side, in person',
    description:
      'Skip the guesswork. A trained stylist travels with you across boutiques and stores in select Indian cities. Honest fit feedback, brand-agnostic curation, and a wardrobe you will actually wear.',
    tags: ['In-Person', 'Wedding Trousseau', 'Wardrobe Refresh', 'Select Cities'],
    cta: 'Explore Personal Shopping',
    link: '/personal-shopping',
    accent: 'charcoal',
    icon: ShoppingBag,
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80&auto=format&fit=crop',
    video: '/suitcase_shopping.mp4',
  },
];

const accentStyles = {
  coral: {
    tagBg: 'bg-coral/10 text-coral-deep',
    button: 'bg-gradient-to-r from-coral to-coral-light hover:shadow-glow-coral',
    blob: 'bg-coral/[0.07]',
    overlayFrom: 'from-coral/80',
    iconBg: 'bg-gradient-to-br from-coral to-coral-light',
    pillBorder: 'border-coral/20',
  },
  teal: {
    tagBg: 'bg-teal/10 text-teal-deep',
    button: 'bg-gradient-to-r from-teal to-teal-light hover:shadow-glow-teal',
    blob: 'bg-teal/[0.07]',
    overlayFrom: 'from-teal/80',
    iconBg: 'bg-gradient-to-br from-teal to-teal-light',
    pillBorder: 'border-teal/20',
  },
  golden: {
    tagBg: 'bg-golden/10 text-golden-deep',
    button: 'bg-gradient-to-r from-golden to-[#f0c078] hover:shadow-lg',
    blob: 'bg-golden/[0.07]',
    overlayFrom: 'from-golden/80',
    iconBg: 'bg-gradient-to-br from-golden to-[#f0c078]',
    pillBorder: 'border-golden/20',
  },
  charcoal: {
    tagBg: 'bg-charcoal/10 text-charcoal',
    button: 'bg-gradient-to-r from-charcoal to-charcoal/80 hover:shadow-lg',
    blob: 'bg-charcoal/[0.05]',
    overlayFrom: 'from-charcoal/80',
    iconBg: 'bg-gradient-to-br from-charcoal to-charcoal/70',
    pillBorder: 'border-charcoal/20',
  },
};

const bgColors = ['bg-off-white', 'bg-light-gray', 'bg-white', 'bg-off-white'];

const CategoryRow: React.FC<{ category: Category; index: number }> = ({ category, index }) => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const styles = accentStyles[category.accent];
  const isReversed = index % 2 === 1;
  const Icon = category.icon;
  const [videoFailed, setVideoFailed] = useState(false);

  return (
    <section className={`relative ${bgColors[index]} overflow-hidden`}>
      {/* Decorative blob */}
      <div
        className={`absolute ${
          isReversed ? 'top-10 left-[-8%]' : 'top-10 right-[-8%]'
        } w-[500px] h-[500px] ${styles.blob} rounded-full blur-[100px] animate-blob${
          index === 1 ? ' animate-blob-delay-2' : index === 2 ? ' animate-blob-delay-4' : index === 3 ? ' animate-blob-delay-2' : ''
        }`}
      />

      <div
        ref={elementRef}
        className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 transition-all duration-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
            isReversed ? 'lg:direction-rtl' : ''
          }`}
        >
          {/* Image */}
          <div
            className={`relative rounded-3xl overflow-hidden shadow-glass-lg ${
              isReversed ? 'lg:order-1' : 'lg:order-2'
            } order-1`}
            style={{ minHeight: '360px' }}
          >
            {category.video && !videoFailed ? (
              <video
                src={`${process.env.PUBLIC_URL}${category.video}`}
                poster={category.image}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-label={category.title}
                className="w-full h-full object-cover absolute inset-0"
                // Render the still image instead if the video can't load —
                // hiding the video would also hide its poster, leaving an
                // empty box.
                onError={() => setVideoFailed(true)}
              />
            ) : (
              <img
                src={category.image}
                alt={category.title}
                className="w-full h-full object-cover absolute inset-0"
                loading="lazy"
              />
            )}
            <div
              className={`absolute inset-0 bg-gradient-to-t ${styles.overlayFrom} via-transparent to-transparent opacity-40`}
            />
          </div>

          {/* Content */}
          <div
            className={`${
              isReversed ? 'lg:order-2 lg:pr-4' : 'lg:order-1 lg:pl-4'
            } order-2`}
          >
            {/* Icon badge */}
            <div
              className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${styles.iconBg} shadow-lg mb-6`}
            >
              <Icon size={26} className="text-white" />
            </div>

            {/* Tagline pill */}
            <div
              className={`inline-flex items-center px-4 py-2 rounded-full glass shadow-glass text-sm font-medium text-charcoal mb-4`}
            >
              {category.tagline}
            </div>

            {/* Title */}
            <h2 className="font-nunito font-bold text-3xl sm:text-4xl lg:text-5xl text-charcoal mb-4">
              {category.title}
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
              {category.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2.5 mb-8">
              {category.tags.map((tag) => (
                <span
                  key={tag}
                  className={`px-4 py-2 rounded-full text-sm font-medium border ${styles.tagBg} ${styles.pillBorder}`}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* CTA */}
            <Link
              to={category.link}
              className={`group inline-flex items-center px-8 py-4 rounded-2xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] ${styles.button}`}
            >
              {category.cta}
              <ArrowRight
                size={18}
                className="ml-2.5 group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const CategoryShowcase: React.FC = () => {
  return (
    <div>
      {categories.map((category, index) => (
        <CategoryRow key={category.title} category={category} index={index} />
      ))}
    </div>
  );
};

export default CategoryShowcase;
